import React, { useState } from "react";
import t from "../../../../../../i18n";
import ReportingChart from "../../../ReportingChart";
import { useSeries } from "./useSeries";
import { useOptions } from "./useOptions";
import useVisibleSeries from "@ui/Highcharts/hooks/useVisibleSeries";
import { DateTime } from "luxon";
import { translationLocale } from "../../../../../../i18n/locale";
import capitalize from "../../../../../../lib/capitalize";
import { useReparabilityMatrixPeriod } from "../useReparabilityMatrixPeriod";
import PeriodSelector from "./PeriodSelector";
import ReparabilityMatrixSelector, { type DisplayMode } from "../../../ReparabilityMatrixSelector";
import ReparabilityImpactMatrixChart from "../ReparabilityImpactMatrixChart";
import type { DiagnosisReporting } from "../../../../../../api/useReporting";
import { useSettings } from "../../../../../../api/useSettings";

const chartDefinition = {
  id: "FleetFailureImpactByReparabilityMatrix",
  title: t("reporting.failure_impact_reparability.title"),
  description: {
    title: t("reporting.chart.info.title"),
    description: t("reporting.failure_impact_reparability.description"),
    options: [
      {
        value: "REPARABILITY",
        label: t("reporting.failure_impact_reparability.options.reparability.title"),
        description: t("reporting.failure_impact_reparability.options.reparability.description"),
      },
      {
        value: "IMPACT",
        label: t("reporting.failure_impact_reparability.options.impact.title"),
        description: t("reporting.failure_impact_reparability.options.impact.description"),
      },
    ],
  },
};

function FleetFailureImpactByReparabilityMatrix({ reporting }: { reporting: DiagnosisReporting }) {
  const chart = React.useRef<Highcharts.Chart>(null);
  const settings = useSettings();
  const series = useSeries(reporting);
  const [visibleSeries] = useVisibleSeries(chartDefinition.id, series);
  const [selectedReparabilityMatrixPeriod] = useReparabilityMatrixPeriod({});
  const options = useOptions(chart, {
    reporting,
    series: visibleSeries,
  });

  const defaultMode: DisplayMode = settings.FLEET_VIEW_REPAIRABILITY_MATRIX ? "matrix" : "chart";
  const [displayMode, setDisplayMode] = useState<DisplayMode>(defaultMode);

  const failureInfos = React.useMemo(() => {
    const failureCount = series.reduce((acc, { data }) => acc + (data?.length || 0), 0);

    const failureCountInfo = t("reporting.chart.subtitle.filtered_failures", {
      failureCount,
    });

    if (!selectedReparabilityMatrixPeriod) return failureCountInfo;

    const dateInfo = capitalize(
      DateTime.fromMillis(selectedReparabilityMatrixPeriod)
        .setLocale(translationLocale)
        .toFormat("MMMM yyyy"),
    );

    return failureCount > 0 ? `${failureCountInfo} — ${dateInfo}` : `${dateInfo}`;
  }, [selectedReparabilityMatrixPeriod, series]);

  if (displayMode === "matrix") {
    return (
      <ReparabilityImpactMatrixChart
        reporting={reporting}
        matrixSelector={
          <ReparabilityMatrixSelector value={displayMode} onChange={setDisplayMode} />
        }
        dateSelector={<PeriodSelector reporting={reporting} />}
        title={chartDefinition.title}
        description={chartDefinition.description}
      />
    );
  }

  return options && series ? (
    <ReportingChart
      chartRef={chart}
      reporting={reporting}
      id={chartDefinition.id}
      title={chartDefinition.title}
      matrixSelector={
        settings.FLEET_VIEW_REPAIRABILITY_MATRIX ? (
          <ReparabilityMatrixSelector value={displayMode} onChange={setDisplayMode} />
        ) : null
      }
      dateSelector={<PeriodSelector reporting={reporting} />}
      extraSubtitleInfo={failureInfos}
      description={chartDefinition.description}
      series={visibleSeries}
      options={options}
    />
  ) : null;
}

export default React.memo(FleetFailureImpactByReparabilityMatrix);
